// Each write owns an immutable ticket snapshot. Navigation never changes its target.
export function createDraftPersistence({ send, onStatus = () => {}, delay = 700 }) {
  const entries = new Map();
  let epoch = 0;
  function notify(entry, status, error) {
    entry.status = status;
    onStatus(entry.id, status, entry.value, error);
  }
  async function flush(id) {
    const entry = entries.get(id);
    if (!entry) return;
    clearTimeout(entry.timer);
    if (entry.running) return entry.running;
    if (!entry.dirty) return;
    const startedEpoch = epoch, revision = entry.revision;
    const value = structuredClone(entry.value);
    notify(entry, 'saving');
    entry.controller = new AbortController();
    entry.running = (async () => {
      try {
        const saved = await send(id, { ...value, expectedDraftVersion: entry.version }, entry.controller.signal);
        if (epoch !== startedEpoch || entries.get(id) !== entry) return;
        entry.version = Number(saved.version || 0);
        entry.value.version = entry.version;
        entry.dirty = entry.revision !== revision;
        notify(entry, entry.dirty ? 'unsaved' : 'saved');
      } catch (error) {
        if (epoch === startedEpoch && entries.get(id) === entry) notify(entry, 'failed', error);
      } finally {
        entry.running = null;
        if (epoch === startedEpoch && entries.get(id) === entry && entry.dirty && !entry.deferred && entry.status !== 'failed') void flush(id);
        // Keep only 20 clean snapshots; never evict unsaved work.
        const clean = [...entries.values()].filter(item => !item.dirty && !item.running);
        for (const item of clean.slice(0, Math.max(0, clean.length - 20))) entries.delete(item.id);
      }
    })();
    return entry.running;
  }
  return {
    snapshot(id, value, { defer = false } = {}) {
      let entry = entries.get(id);
      if (!entry) { entry = { id, version: Number(value.version || 0), revision: 0 }; entries.set(id, entry); }
      entry.value = structuredClone(value); entry.revision++; entry.dirty = true; entry.deferred = defer;
      clearTimeout(entry.timer);
      notify(entry, 'unsaved');
      if (!defer) entry.timer = setTimeout(() => void flush(id), delay);
    },
    get(id) { const entry = entries.get(id); return entry ? structuredClone(entry.value) : null; },
    busy(id) { return Boolean(entries.get(id)?.running); },
    rebase(id, version) { const entry = entries.get(id); if (entry) { entry.version = Number(version || 0); entry.value.version = entry.version; entry.deferred = false; } },
    flush,
    remove(id) { const entry = entries.get(id); clearTimeout(entry?.timer); entries.delete(id); },
    reset() { epoch++; for (const entry of entries.values()) { clearTimeout(entry.timer); entry.controller?.abort(); } entries.clear(); }
  };
}
